/**
 * Mutation with Invalidation Helper
 *
 * Helper function for executing mutations outside of React hook context
 * while still triggering the centralized cache invalidation from MUTATION_INVALIDATION_MAP.
 *
 * The signature mirrors how you'd use TanStack Query hooks - pass the mutation options
 * (from Orval-generated getMutationOptions functions) and the variables.
 *
 * @example
 * // Similar to how you'd use the hook:
 * // const { mutate } = usePostRequestName({ request: buildRequestOptions() })
 * // mutate({ params: { ...commonParams, name } })
 *
 * // Use the imperative helper:
 * await mutationWithInvalidation(
 *     getPostRequestNameMutationOptions({ request: buildRequestOptions() }),
 *     { params: { ...buildCommonParams(), name } },
 * )
 */

import type { MutationKey, UseMutationOptions } from '@tanstack/react-query'

import { getQueryClient } from '.'
import { getInvalidationHandler } from './mutationInvalidationMap'

/**
 * Execute a mutation and trigger the corresponding invalidation handler.
 *
 * Use this for imperative mutations outside of React hook context (e.g., route loaders,
 * Redux thunks, or other non-component code) that need to trigger the centralized
 * cache invalidation defined in MUTATION_INVALIDATION_MAP.
 *
 * @param mutationOptions - Orval-generated mutation options (from getMutationOptions functions)
 * @param variables - The variables to pass to the mutation function
 * @returns The result of the mutation
 *
 * @example
 * export async function mutateSetProfileName(name: string) {
 *     return mutationWithInvalidation(
 *         getPostRequestNameMutationOptions({ request: buildRequestOptions() }),
 *         { params: { ...buildCommonParams(), name } },
 *     )
 * }
 */
export async function mutationWithInvalidation<TData, TError, TVariables, TContext>(
    mutationOptions: UseMutationOptions<TData, TError, TVariables, TContext> & {
        mutationKey?: MutationKey
    },
    variables: TVariables,
): Promise<TData> {
    const { mutationFn, mutationKey } = mutationOptions

    if (!mutationFn) {
        throw new Error('mutationWithInvalidation requires mutationFn in options')
    }

    const queryClient = getQueryClient()

    // Create a minimal context for the mutation function.
    // Orval-generated mutation functions don't use the context, but the type signature requires it.
    const context = {
        client: queryClient,
        meta: undefined,
        mutationKey,
    }

    const result = await mutationFn(variables, context)

    const invalidationHandler = getInvalidationHandler(mutationKey)
    await invalidationHandler?.(queryClient, variables)

    return result
}
